import { useCallback } from 'react';
import { z } from 'zod';
import { FormFieldsSchema } from '@ninja/utilities';
import { useCountries } from '../../../../app-init-data';
import { prepareFieldError } from '../../../members/members-form/helpers/add-field-error';
import { includeVisaFileNumberField } from '../../utilities';
import { useOccupationField, usePassportNumberField, useSalaryBandField, useVisaFileNumberField } from '../fields';
import { DocumentsForm, MemberDocumentForm } from '../types';

export const useCommonFieldsStructure = () => {
	const passportNumberField = usePassportNumberField();
	const salaryBandField = useSalaryBandField();
	const occupationField = useOccupationField();
	const visaFileNumberField = useVisaFileNumberField();
	const { getCountryById } = useCountries();
	const getCommonFieldStructure = useCallback(
		(
			memberId: string,
			includeVisaFileNumberField: boolean,
			includeSalaryBandField: boolean,
			disableSalaryBandField: boolean,
			includeOccupationField: boolean
		) => {
			const fields: FormFieldsSchema<DocumentsForm> = [passportNumberField.getField(memberId)];

			if (includeVisaFileNumberField) {
				fields.push(visaFileNumberField.getField(memberId));
			}

			if (includeSalaryBandField) {
				fields.push(salaryBandField.getField(memberId, disableSalaryBandField));
			}

			if (includeOccupationField) {
				fields.push(occupationField.getField(memberId));
			}

			return fields;
		},
		[passportNumberField.getField, visaFileNumberField.getField, salaryBandField.getField, occupationField.getField]
	);

	const getValidations = () =>
		z.object({
			passportNumber: passportNumberField.getValidation(),
			visaFileNumber: visaFileNumberField.getValidation(),
			salaryBand: salaryBandField.getValidation(),
			occupationId: occupationField.getValidation(),
		});

	const validate = (
		memberDocumentFormValue:
			| Pick<MemberDocumentForm, 'passportNumber' | 'visaFileNumber' | 'salaryBand' | 'occupationId'>
			| undefined,
		options: {
			passportNumberUseCount: number;
			visaFileNumberUseCount: number;
			validateSalaryBand: boolean;
			nationalityId?: number;
			validatedOccupationId?: boolean;
			occupationId?: number;
		},
		addFieldError: ReturnType<typeof prepareFieldError>
	) => {
		passportNumberField.validate(
			memberDocumentFormValue?.passportNumber,
			options.passportNumberUseCount,
			addFieldError
		);

		options.validatedOccupationId && occupationField.validate(options.occupationId, addFieldError);

		if (includeVisaFileNumberField(getCountryById(options.nationalityId!))) {
			visaFileNumberField.validate(
				memberDocumentFormValue?.visaFileNumber,
				options.visaFileNumberUseCount,
				addFieldError
			);
		}
		options.validateSalaryBand && salaryBandField.validate(memberDocumentFormValue?.salaryBand, addFieldError);
	};

	return {
		getCommonFieldStructure,
		getValidations,
		validate,
	};
};
